import React, { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { ChevronDown, X, Search } from 'lucide-react';

export const MultiSelectFilter = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select...',
  icon: Icon,
  className = '',
  maxDisplay = 2,
  searchable = false,
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && searchable && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!open) setSearch('');
  }, [open, searchable]);

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter(v => v !== val)
      : [...selected, val];
    onChange(next);
  };

  const selectAll = () => onChange(options.map(o => o.value));
  const clearAll = () => onChange([]);

  const displayLabel = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length <= maxDisplay) {
      return selected.map(v => options.find(o => o.value === v)?.label || v).join(', ');
    }
    return `${selected.length} selected`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between gap-1 font-normal dark:bg-slate-900 dark:border-slate-700 ${className}`}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate text-sm">
            {Icon && <Icon className="w-4 h-4 text-muted-foreground shrink-0" />}
            <span className="truncate">{displayLabel()}</span>
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {selected.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] rounded-full">
                {selected.length}
              </Badge>
            )}
            {selected.length > 0 ? (
              <X
                className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); clearAll(); }}
              />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0 dark:bg-slate-800 dark:border-slate-700" align="start">
        {searchable && (
          <div className="p-2 border-b dark:border-slate-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                ref={inputRef}
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-7 text-sm"
              />
            </div>
          </div>
        )}
        <div className="flex items-center justify-between px-3 py-1.5 border-b dark:border-slate-700">
          <button onClick={selectAll} className="text-[11px] text-[#4A9CC7] hover:underline" data-testid={testId ? `${testId}-select-all` : undefined}>
            Select all
          </button>
          <button onClick={clearAll} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid={testId ? `${testId}-clear` : undefined}>
            Clear
          </button>
        </div>
        <div className="max-h-[240px] overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No results</div>
          )}
          {filtered.map(option => (
            <label
              key={option.value}
              className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors"
              data-testid={testId ? `${testId}-option-${option.value}` : undefined}
            >
              <Checkbox
                checked={selected.includes(option.value)}
                onCheckedChange={() => toggle(option.value)}
                className="h-4 w-4"
              />
              <span className="flex items-center gap-2 text-sm truncate">
                {option.dot && <div className={`w-2 h-2 rounded-full shrink-0 ${option.dot}`} />}
                {option.icon && <option.icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                <span className="truncate">{option.label}</span>
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectFilter;
